import { QueryClient } from '@tanstack/react-query';
import { API_CONFIG } from '../config/apiConfig.js';
import { toastService } from '../services/toastService.js';
import { logger } from '../utils/logger.js';

/**
 * Instance unique et centralisée de QueryClient pour toute l'application EKOSE.
 * Cette configuration unifie les paramètres de cache, de retry et de gestion d'erreurs.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Configuration basée sur API_CONFIG pour la cohérence
      staleTime: API_CONFIG?.CACHE?.STALE_TIME || 30000,
      
      // Retry intelligent avec backoff exponentiel configuré
      retry: (failureCount: number, error: any) => {
        // Ne pas retry sur les erreurs d'authentification (401)
        if (error?.status === 401 || error?.response?.status === 401) return false;

        // Retry limité sur rate limiting (429)
        if (error?.status === 429 || error?.response?.status === 429) return failureCount < 2;

        // Retry standard pour les autres erreurs
        const maxAttempts = (API_CONFIG?.RETRY_CONFIG?.attempts as number) || 3;
        return failureCount < maxAttempts;
      },

      // Délai de retry progressif
      retryDelay: (attemptIndex: number) => {
        const baseDelay = (API_CONFIG?.RETRY_CONFIG?.delay as number) || 1000;
        const multiplier = (API_CONFIG?.RETRY_CONFIG?.backoffMultiplier as number) || 2;
        const maxDelay = (API_CONFIG?.RETRY_CONFIG?.maxDelay as number) || 30000;
        
        const delay = baseDelay * Math.pow(multiplier, attemptIndex);
        return Math.min(delay, maxDelay);
      },

      // Refetch au focus et reconnexion activés pour assurer la fraîcheur des données dashboard
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      
      // Pas de polling global (le temps réel est géré par WebSocket)
      refetchInterval: false,
      
      // Mode réseau par défaut
      networkMode: 'online',
    },
    mutations: {
      // Retry limité pour les mutations pour éviter les doublons accidentels
      retry: 1,
      networkMode: 'online',
    },
  },
});

// Exposer le queryClient globalement comme fallback de transition (compatibilité legacy)
if (typeof window !== 'undefined') {
  (window as any).queryClient = queryClient;
}

export default queryClient;
